import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FiPlus, FiChevronDown, FiSearch } from 'react-icons/fi';
import { useDatabase } from '../../hooks/useDatabase';
import { useCurrentPerson } from '../../hooks/useCurrentPerson';
import PersonMappingPrompt from '../../components/common/PersonMappingPrompt';
import NativeTeamForm from '../../components/forms/NativeTeamForm';
import TeamDashboard from '../../components/dashboards/TeamDashboard';

const YourTeamsPage: React.FC = () => {
  const { database, applyCommand } = useDatabase();
  const { personId, setPersonId, teamIds, isMapped, loading: personLoading } = useCurrentPerson();
  const [activeTeamId, setActiveTeamId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinPicker, setShowJoinPicker] = useState(false);
  const [joinQuery, setJoinQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Teams the user is NOT already a member of
  const joinableTeams = useMemo(() => {
    if (!database) return [];
    const currentSet = new Set(teamIds);
    const all = database.teams();
    return Object.entries(all)
      .filter(([id]) => !currentSet.has(id))
      .map(([id, t]) => ({ id, name: (t as any).name() }));
  }, [database, teamIds]);

  const filteredJoinable = useMemo(() => {
    if (!joinQuery.trim()) return joinableTeams;
    const q = joinQuery.toLowerCase();
    return joinableTeams.filter(t => t.name.toLowerCase().includes(q));
  }, [joinableTeams, joinQuery]);

  const joinTeam = async (teamId: string) => {
    if (!personId) return;
    await applyCommand({
      type: 'SetTeamMembership',
      membership: {
        member: { Person: personId },
        team: teamId,
      },
      enabled: true,
    });
    setShowJoinPicker(false);
    setJoinQuery('');
    setActiveTeamId(teamId);
  };

  // Resolve team names for the tabs
  const teams = useMemo(() => {
    if (!database || teamIds.length === 0) return [];
    const teamsMap = database.teams();
    return teamIds
      .map((id) => {
        const t = teamsMap[id];
        return t ? { id, name: t.name() } : null;
      })
      .filter(Boolean) as { id: string; name: string }[];
  }, [database, teamIds]);

  const selectedTeamId = activeTeamId || (teams.length > 0 ? teams[0].id : null);

  useEffect(() => {
    if (!showDropdown) return;
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showDropdown]);

  if (personLoading) {
    return (
      <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isMapped) {
    return <PersonMappingPrompt onSelect={setPersonId} />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Your Teams</h1>
          <p className="mt-1 text-sm text-cm-text-secondary">
            View and manage your teams, forecasts, and deadlines.
          </p>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cm-accent-orange text-cm-bg-deep font-mono text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            Add team
            <FiChevronDown className="w-3.5 h-3.5" />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-1 w-48 bg-cm-bg-elevated border border-cm-border-mid rounded-lg shadow-xl z-50 py-1">
              <button
                className="w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                onClick={() => { setShowDropdown(false); setShowJoinPicker(true); setJoinQuery(''); }}
              >
                Join an existing team
              </button>
              <button
                className="w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                onClick={() => { setShowDropdown(false); setShowCreateForm(true); }}
              >
                Create a new team
              </button>
            </div>
          )}
        </div>
      </div>

      {teams.length === 0 ? (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-8 text-center">
          <p className="text-cm-text-muted mb-4">You are not a member of any teams yet.</p>
          <p className="text-sm text-cm-text-muted">
            Use the <span className="font-medium text-cm-accent-orange">Add team</span> button above to create or join a team.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-cm-border-subtle overflow-x-auto">
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => setActiveTeamId(team.id)}
                className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  selectedTeamId === team.id
                    ? 'border-cm-accent-orange text-cm-accent-orange font-medium'
                    : 'border-transparent text-cm-text-muted hover:text-cm-text-secondary hover:border-cm-border-mid'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>

          {selectedTeamId && (
            <div className="mt-4">
              <TeamDashboard key={selectedTeamId} teamId={selectedTeamId} />
            </div>
          )}
        </>
      )}

      <NativeTeamForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={applyCommand}
        onCreated={(newTeamId) => joinTeam(newTeamId)}
      />

      {/* Join existing team picker */}
      {showJoinPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-cm-bg-deep/70" onClick={() => setShowJoinPicker(false)} />
          <div className="relative bg-cm-bg-surface border border-cm-border-mid rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6">
            <h3 className="font-mono font-semibold text-cm-text-primary mb-4">Join a team</h3>
            <div className="relative mb-3">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cm-text-muted" />
              <input
                type="text"
                value={joinQuery}
                onChange={(e) => setJoinQuery(e.target.value)}
                placeholder="Search teams..."
                autoFocus
                className="w-full pl-10 pr-4 py-2 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-orange focus:ring-cm-accent-orange"
              />
            </div>
            {filteredJoinable.length > 0 ? (
              <div className="max-h-48 overflow-y-auto divide-y divide-cm-border-subtle border border-cm-border-subtle rounded-lg">
                {filteredJoinable.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => joinTeam(t.id)}
                    className="w-full px-4 py-3 text-sm text-cm-text-primary hover:bg-cm-bg-hover transition-colors text-left"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            ) : joinableTeams.length === 0 ? (
              <p className="text-sm text-cm-text-muted text-center py-4">All teams are already joined.</p>
            ) : (
              <p className="text-sm text-cm-text-muted text-center py-4">No teams match "{joinQuery}"</p>
            )}
            <button
              onClick={() => setShowJoinPicker(false)}
              className="mt-4 w-full text-center text-sm text-cm-text-muted hover:text-cm-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YourTeamsPage;
